/**
 * featureRegistry — every sellable feature key (backend: seed_features.py) mapped to the real routes and
 * buttons it unlocks. This is what "each feature is part of the application" means in code: a key with
 * no route and no button can't exist here -- aibuddy-lab-core-api/tests/unit/test_feature_registry.py fails if a
 * seeded feature is missing from this file or has neither a route nor a button, so it can't be sold.
 *
 * Route patterns use react-router syntax; `routeFeature()` finds the feature guarding a path.
 */

export interface FeatureEntry {
  key: string;
  /** Short name used in lock badges / upgrade prompts (the full marketing name comes from the catalog). */
  label: string;
  routes: string[];
  /** Where the button lives, for humans reading this file (not used for matching). */
  buttons: string[];
}

export const FEATURE_REGISTRY: FeatureEntry[] = [
  // Learn with AI
  { key: 'ai_classroom', label: 'AI Classroom', routes: ['/my-instructor'], buttons: ['My Instructor ▶ Start (board lessons)'] },
  { key: 'live_voice_tutor', label: 'AI Voice Teacher', routes: [], buttons: ['My Instructor ▶ Start (voice)', 'My Instructor ✋ Ask'] },
  { key: 'avatar_live_tutor', label: 'Face-to-face AI Avatar', routes: [], buttons: ['My Instructor avatar toggle'] },
  { key: 'study_material', label: 'Study Material', routes: ['/study-material/:courseId/:topicId'], buttons: ['My Instructor 🗂️ Study Material'] },
  { key: 'lesson_plan', label: 'Lesson Plan', routes: ['/lesson-plan/:courseId/:topicId'], buttons: ['My Instructor 📚 Lesson Plan'] },
  { key: 'activity_boards', label: 'Examples & Use Cases', routes: [], buttons: ['My Instructor 🧩 Example', 'My Instructor 💡 Use Case'] },
  { key: 'ask_question', label: 'Ask Question', routes: ['/ask-question'], buttons: [] },
  { key: 'replay_academy', label: 'Replay Academy', routes: ['/replay-academy'], buttons: [] },
  { key: 'knowledge_capsules', label: 'Knowledge Capsules', routes: ['/knowledge-capsules/:id/learn'], buttons: [] },
  { key: 'language_learning', label: 'Language Learning', routes: ['/language-learning', '/language-learning/lessons/:lessonId', '/language-learning/lessons/:lessonId/progress', '/language-learning/activity/:sessionId', '/language-learning/progress'], buttons: [] },
  // Practice & Feedback
  { key: 'quiz', label: 'Quizzes', routes: [], buttons: ['My Instructor 📝 Quiz'] },
  { key: 'assignment', label: 'Assignments', routes: [], buttons: ['My Instructor ✏️ Assignment', 'My Instructor ⬆️ Submit'] },
  { key: 'multiple_test', label: 'Course Tests', routes: [], buttons: ['Course Test start'] },
  { key: 'reading_assessment', label: 'Reading Coach', routes: ['/reading-assessment/:topicId', '/reading-reports'], buttons: ['My Instructor "Start Reading"'] },
  { key: 'handwriting_practice', label: 'Handwriting Coach', routes: ['/handwriting-practice/:courseId/:topicId'], buttons: ['My Instructor ✍️ Handwriting'] },
  { key: 'doubt_session', label: 'Doubt Help Desk', routes: ['/doubt-session', '/doubt-session/new', '/doubt-session/:id/edit', '/tickets', '/tickets/:id'], buttons: [] },
  // Progress & Reports
  { key: 'progress_report', label: 'AI Progress Report', routes: [], buttons: ['My Instructor 📊 Progress Report'] },
  { key: 'calendar_scheduling', label: 'Study Calendar', routes: ['/calendar'], buttons: [] },
  // Live Classes
  { key: 'join_live_class', label: 'Join Live Classes', routes: ['/join-class'], buttons: [] },
  { key: 'live_class', label: 'Host Live Classes', routes: ['/live-class', '/my-classes', '/my-teaching'], buttons: [] },
  // Teaching Tools
  { key: 'course_builder', label: 'Course Builder', routes: ['/course-management', '/course-management/:courseId', '/create-course', '/courses/design-with-ai', '/courses/design-with-ai/:sessionId'], buttons: [] },
  { key: 'record_with_teacher', label: 'Record with Teacher', routes: ['/topic-recorder'], buttons: ['Topic editor 🎥 Record with teacher'] },
  { key: 'test_management', label: 'Test Builder', routes: ['/test-management'], buttons: [] },
  { key: 'content_management', label: 'Content Management', routes: ['/replay-academy-management', '/knowledge-capsules', '/calendar-management'], buttons: [] },
  { key: 'school_admin', label: 'School Console', routes: ['/school/:tab'], buttons: [] },
];

export const FEATURE_BY_KEY: Record<string, FeatureEntry> = Object.fromEntries(FEATURE_REGISTRY.map((f) => [f.key, f]));

/** react-router style pattern -> regex (":param" matches one segment). */
const patternToRegex = (pattern: string) =>
  new RegExp(`^${pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\:([a-zA-Z]+)/g, '[^/]+').replace(/:([a-zA-Z]+)/g, '[^/]+')}/?$`);

const ROUTE_MATCHERS = FEATURE_REGISTRY.flatMap((f) => f.routes.map((r) => ({ key: f.key, re: patternToRegex(r) })));

/** The feature that gates a concrete path (e.g. "/reading-assessment/abc" -> "reading_assessment"), if any. */
export function routeFeature(pathname: string): string | undefined {
  return ROUTE_MATCHERS.find((m) => m.re.test(pathname))?.key;
}
